import { FC, memo, useMemo, useRef, useState } from 'react'
import { SxProps } from '@mui/material'
import { Swiper as SwiperReact, SwiperClass, SwiperSlide } from 'swiper/react'
import { Navigation, Virtual } from 'swiper/modules'
import clsx from 'clsx'
import { Box } from '@/components/Layout'
import { withErrorBoundary } from '@/components/Utils'

import { swiperStyles as styles } from './Swiper.style'
import { SwiperSliderProps } from './Swiper.types'
import SwiperButtons from './SwiperButtons'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/virtual'
import { swiperClasses } from './Swiper.data'
import { hasContentInComponent } from '@/utils'

const Swiper: FC<SwiperSliderProps> = ({
  children,
  showNavigationButton = true,
  wrapperProps = {},
  swiperWrapperProps = {},
  HeaderContent,
  headerWrapperProps = {},
  modules = [],
  virtual,
  ...props
}) => {
  const [swiperRef, setSwiperRef] = useState<SwiperClass | null>(null)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  const navigationPrevButtonRef = useRef<HTMLButtonElement>(null)
  const navigationNextButtonRef = useRef<HTMLButtonElement>(null)

  const isVirtual = Boolean(virtual)
  const virtualConfig = useMemo(
    () =>
      isVirtual
        ? { addSlidesBefore: 2, addSlidesAfter: 2, ...(typeof virtual === 'object' ? virtual : {}) }
        : undefined,
    [isVirtual, virtual],
  )
  const modulesConfig = useMemo(
    () => (isVirtual ? [Navigation, Virtual, ...modules] : [Navigation, ...modules]),
    [isVirtual, modules],
  )

  const { onReachBeginning, onReachEnd, onSlideChange, navigation = {} } = props
  const { sx: swiperWrapperSx, ...othersSwiperWrapperProps } = swiperWrapperProps
  const { sx: wrapperSx, ...otherWrapperProps } = wrapperProps
  const {
    sx: headerWrapperSx,
    className: headerWrapperClassName = '',
    ...otherHeaderWrapperProps
  } = headerWrapperProps

  const hasAdditionalHeaderContent = hasContentInComponent(HeaderContent)
  return (
    <Box
      sx={[styles.wrapper, wrapperSx] as SxProps}
      onClick={(event) => event.stopPropagation()}
      {...otherWrapperProps}>
      <Box
        sx={[styles.headerWrapper, headerWrapperSx] as SxProps}
        className={clsx({
          [swiperClasses.headerClasses.withHeader]:
            hasAdditionalHeaderContent || showNavigationButton,
          [swiperClasses.headerClasses.withAdditionalContent]: hasAdditionalHeaderContent,
          [headerWrapperClassName]: Boolean(headerWrapperClassName),
        })}
        {...otherHeaderWrapperProps}>
        {HeaderContent}

        <SwiperButtons
          isBeginning={isBeginning}
          isEnd={isEnd}
          navigationPrevButtonRef={navigationPrevButtonRef}
          navigationNextButtonRef={navigationNextButtonRef}
          showNavigationButton={showNavigationButton}
        />
      </Box>

      <Box sx={[styles.swiperWrapper, swiperWrapperSx] as SxProps} {...othersSwiperWrapperProps}>
        <SwiperReact
          modules={modulesConfig}
          virtual={virtualConfig}
          onSwiper={setSwiperRef}
          slidesPerView={'auto'}
          spaceBetween={8}
          onReachBeginning={(swiper: SwiperClass) => {
            setIsBeginning(true)
            onReachBeginning?.(swiper)
          }}
          onReachEnd={(swiper: SwiperClass) => {
            setIsEnd(true)
            onReachEnd?.(swiper)
          }}
          onSlideChange={(swiper: SwiperClass) => {
            setIsBeginning(swiperRef?.isBeginning || false)
            setIsEnd(swiperRef?.isEnd || false)
            onSlideChange?.(swiper)
          }}
          navigation={{
            prevEl: navigationPrevButtonRef.current,
            nextEl: navigationNextButtonRef.current,
            ...(typeof navigation === 'object' ? navigation : {}),
          }}
          {...props}>
          {children}
        </SwiperReact>
      </Box>
    </Box>
  )
}

export { SwiperSlide }
export type { SwiperClass }

export default memo(withErrorBoundary(Swiper))
